import { FC, useCallback, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TipTapImage from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { Button, Divider, Tooltip, message } from 'antd'
import {
	BoldOutlined,
	ItalicOutlined,
	UnderlineOutlined,
	StrikethroughOutlined,
	OrderedListOutlined,
	UnorderedListOutlined,
	LinkOutlined,
	PictureOutlined,
	CodeOutlined,
	MinusOutlined,
	AlignLeftOutlined,
	AlignCenterOutlined,
	AlignRightOutlined
} from '@ant-design/icons'
import { uploadLearningFileApi } from '../api/learning'
import '../styles/tiptap.scss'

interface RichTextEditorProps {
	value?: string
	onChange?: (html: string) => void
	placeholder?: string
	minHeight?: number
}

interface ToolbarButtonProps {
	onClick: () => void
	active?: boolean
	icon: React.ReactNode
	tooltip: string
}

const ToolbarButton: FC<ToolbarButtonProps> = ({
	onClick,
	active,
	icon,
	tooltip
}) => (
	<Tooltip title={tooltip} mouseEnterDelay={0.5}>
		<Button
			size="small"
			type={active ? 'primary' : 'text'}
			icon={icon}
			style={{ minWidth: 28, padding: '0 4px' }}
			onMouseDown={(e) => {
				e.preventDefault()
				onClick()
			}}
		/>
	</Tooltip>
)

export const RichTextEditor: FC<RichTextEditorProps> = ({
	value = '',
	onChange,
	placeholder = 'Начните вводить текст…',
	minHeight = 320
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null)

	const editor = useEditor({
		extensions: [
			StarterKit,
			Underline,
			TipTapImage.configure({ inline: false, allowBase64: false }),
			Link.configure({ openOnClick: false, autolink: true }),
			Placeholder.configure({ placeholder }),
			TextAlign.configure({ types: ['heading', 'paragraph'] })
		],
		content: value,
		onUpdate: ({ editor }) => {
			onChange?.(editor.getHTML())
		}
	})

	const handleImageFile = useCallback(
		async (file: File) => {
			if (!file.type.startsWith('image/')) {
				message.error('Принимаются только изображения')
				return
			}
			if (file.size > 15 * 1024 * 1024) {
				message.error('Максимальный размер изображения — 15 МБ')
				return
			}
			const hide = message.loading('Загрузка изображения…', 0)
			try {
				const res = await uploadLearningFileApi(file)
				editor?.chain().focus().setImage({ src: res.fileUrl, alt: file.name }).run()
			} catch {
				message.error('Не удалось загрузить изображение')
			} finally {
				hide()
			}
		},
		[editor]
	)

	const handleSetLink = useCallback(() => {
		const previous = editor?.getAttributes('link').href as string | undefined
		const url = window.prompt('Введите URL:', previous || 'https://')
		if (url === null) return
		if (url === '') {
			editor?.chain().focus().extendMarkRange('link').unsetLink().run()
			return
		}
		editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
	}, [editor])

	if (!editor) return null

	return (
		<div
			style={{
				border: '1px solid #d9d9d9',
				borderRadius: 8,
				overflow: 'hidden',
				background: '#fff'
			}}>
			{/* ── Toolbar ── */}
			<div
				style={{
					padding: '6px 10px',
					borderBottom: '1px solid #f0f0f0',
					background: '#fafafa',
					display: 'flex',
					flexWrap: 'wrap',
					gap: 2,
					alignItems: 'center',
					userSelect: 'none'
				}}>
				{/* Text style */}
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleBold().run()}
					active={editor.isActive('bold')}
					icon={<BoldOutlined />}
					tooltip="Жирный (Ctrl+B)"
				/>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleItalic().run()}
					active={editor.isActive('italic')}
					icon={<ItalicOutlined />}
					tooltip="Курсив (Ctrl+I)"
				/>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					active={editor.isActive('underline')}
					icon={<UnderlineOutlined />}
					tooltip="Подчёркнутый (Ctrl+U)"
				/>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleStrike().run()}
					active={editor.isActive('strike')}
					icon={<StrikethroughOutlined />}
					tooltip="Зачёркнутый"
				/>

				<Divider
					type="vertical"
					style={{ margin: '2px 4px', height: 18 }}
				/>

				{/* Headings */}
				{([1, 2, 3] as const).map((level) => (
					<ToolbarButton
						key={level}
						onClick={() =>
							editor
								.chain()
								.focus()
								.toggleHeading({ level })
								.run()
						}
						active={editor.isActive('heading', { level })}
						icon={
							<span
								style={{
									fontWeight: 700,
									fontSize: 11,
									lineHeight: 1
								}}>
								H{level}
							</span>
						}
						tooltip={`Заголовок ${level}`}
					/>
				))}

				<Divider
					type="vertical"
					style={{ margin: '2px 4px', height: 18 }}
				/>

				{/* Alignment */}
				<ToolbarButton
					onClick={() =>
						editor
							.chain()
							.focus()
							.setTextAlign('left')
							.run()
					}
					active={editor.isActive({ textAlign: 'left' })}
					icon={<AlignLeftOutlined />}
					tooltip="По левому краю"
				/>
				<ToolbarButton
					onClick={() =>
						editor
							.chain()
							.focus()
							.setTextAlign('center')
							.run()
					}
					active={editor.isActive({ textAlign: 'center' })}
					icon={<AlignCenterOutlined />}
					tooltip="По центру"
				/>
				<ToolbarButton
					onClick={() =>
						editor
							.chain()
							.focus()
							.setTextAlign('right')
							.run()
					}
					active={editor.isActive({ textAlign: 'right' })}
					icon={<AlignRightOutlined />}
					tooltip="По правому краю"
				/>

				<Divider
					type="vertical"
					style={{ margin: '2px 4px', height: 18 }}
				/>

				{/* Lists */}
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleBulletList().run()
					}
					active={editor.isActive('bulletList')}
					icon={<UnorderedListOutlined />}
					tooltip="Маркированный список"
				/>
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleOrderedList().run()
					}
					active={editor.isActive('orderedList')}
					icon={<OrderedListOutlined />}
					tooltip="Нумерованный список"
				/>

				<Divider
					type="vertical"
					style={{ margin: '2px 4px', height: 18 }}
				/>

				{/* Block elements */}
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleBlockquote().run()
					}
					active={editor.isActive('blockquote')}
					icon={
						<span
							style={{ fontSize: 14, fontStyle: 'italic' }}>
							❝
						</span>
					}
					tooltip="Цитата"
				/>
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleCodeBlock().run()
					}
					active={editor.isActive('codeBlock')}
					icon={<CodeOutlined />}
					tooltip="Блок кода"
				/>
				<ToolbarButton
					onClick={() =>
						editor
							.chain()
							.focus()
							.setHorizontalRule()
							.run()
					}
					icon={<MinusOutlined />}
					tooltip="Горизонтальная линия"
				/>

				<Divider
					type="vertical"
					style={{ margin: '2px 4px', height: 18 }}
				/>

				{/* Link & image */}
				<ToolbarButton
					onClick={handleSetLink}
					active={editor.isActive('link')}
					icon={<LinkOutlined />}
					tooltip="Ссылка"
				/>
				<Tooltip title="Вставить изображение" mouseEnterDelay={0.5}>
					<Button
						size="small"
						type="text"
						icon={<PictureOutlined />}
						style={{ minWidth: 28, padding: '0 4px' }}
						onMouseDown={(e) => {
							e.preventDefault()
							fileInputRef.current?.click()
						}}
					/>
				</Tooltip>

				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					style={{ display: 'none' }}
					onChange={(e) => {
						const file = e.target.files?.[0]
						if (file) handleImageFile(file)
						e.target.value = ''
					}}
				/>
			</div>

			{/* ── Editor area ── */}
			<EditorContent
				editor={editor}
				className="tiptap-prose"
				style={{
					padding: '16px 20px',
					minHeight,
					cursor: 'text'
				}}
			/>
		</div>
	)
}
